import React, { Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  ContentFilterInstagramView,
  ContentFilterInstagramViewProps,
  IGSearchTypesEnum
} from './ContentFilterInstagram.view';
import { UseFormReturn, useWatch } from 'react-hook-form';
import {
  GraphQLTypes,
  ImportSourceArgsGroupInput,
  InstagramApiType,
  InstagramSearchSortType,
  InstagramSearchType,
  SearchInstagramPostsQueryVariables,
  SocialOAuthProvider,
  SocialProfileFields_InstagramSocialProfile_Fragment,
  ThirdPartyProfile
} from '../../../../models/graphql';
import { FindContentContext, SearchStepEnum } from '../FindContentProvider';
import { getApolloErrorMessage, remapApolloErrors } from '../../../../helpers/remapApolloErrors';
import { ApolloError, useApolloClient } from '@apollo/client';
import { useDidMount } from '../../../../hooks/useDidMount';
import { PostSourceType } from '../../../../constants/find-content';
import { fetchInstagramParameters } from './ContentFilterInstagram.apollo';
import { TreeMenuContext } from '../TreeMenu/TreeMenuProvider';
import { FormElementValueType } from '../../../../models/basic-objects';
import { showToast } from '../../../../helpers/showToast';
import { getAllApolloCacheEntitiesOfType } from '../../../../helpers/apolloCacheHelpers';

export type ContentFilterInstagramControllerProps = {
  formInstance: UseFormReturn;
  showFilters: boolean;
  searchParameters: fetchInstagramParameters | undefined;
  setSearchParameters: Dispatch<SetStateAction<fetchInstagramParameters | undefined>>;
  setShowFilters: (show: boolean) => void;
  thirdPartyProfiles: ThirdPartyProfile[];
  fetchThirdPartyProfiles: (profileId: string, profileName: string) => Promise<void>;
  fetchInstagramPosts: (parameters: SearchInstagramPostsQueryVariables) => Promise<void>;
  purgeInstagramCache: () => void;
  purgeThirdPartyProfiles: () => void;
  autoCompleteLoading: boolean;
  profileSearchHistory: ThirdPartyProfile[];
};

export const ContentFilterInstagramController = (props: ContentFilterInstagramControllerProps): JSX.Element => {
  const {
    formInstance,
    searchParameters,
    setSearchParameters,
    fetchInstagramPosts,
    setShowFilters,
    // instagramProfiles,
    fetchThirdPartyProfiles,
    purgeInstagramCache,
    purgeThirdPartyProfiles,
    thirdPartyProfiles
  } = props;
  const typeValue = useWatch({ name: 'type', control: formInstance.control });
  const {
    posts,
    error,
    setError,
    setSourceType,
    setSelectedPosts,
    profiles,
    setSearchStep,
    setHasSearched,
    onSearchResult
  } = useContext(FindContentContext);
  const { modalType, selectedMenuItem, setSelectedMenuItem } = useContext(TreeMenuContext);
  const [apolloErrorKey, setApolloErrorKey] = useState<string | undefined>(undefined);
  const [showSaveSourceModal, setShowSaveSourceModal] = useState<boolean>(false);
  const [selectedAutoCompleteValue, setSelectedAutoCompleteValue] = useState<FormElementValueType>('');
  const { cache } = useApolloClient();

  const clearFilterBarForm = useCallback(() => {
    const elementNames = ['sortType', 'term'];
    elementNames.forEach(e => formInstance.resetField(e));

    setSearchStep(SearchStepEnum.ONE);
  }, [formInstance, setSearchStep]);

  const clearInstagramForm = useCallback(
    (excluded: string) => {
      const elementNames = [
        ...Object.values(IGSearchTypesEnum),
        ...Object.values(InstagramSearchType),
        'sortType',
        'term'
      ];

      // Clear 3rd party profiles for auto-complete
      purgeThirdPartyProfiles();

      purgeInstagramCache();
      setShowFilters(false);

      elementNames
        .filter(n => n !== excluded)
        .forEach(e => {
          formInstance.setValue(e, null);
        });
    },
    [formInstance, purgeThirdPartyProfiles, purgeInstagramCache, setShowFilters]
  );

  const instagramProfiles = useMemo(() => {
    return profiles.filter(
      p => p.__typename === 'InstagramSocialProfile'
    ) as SocialProfileFields_InstagramSocialProfile_Fragment[];
  }, [profiles]);

  const getInstagramPosts = useCallback(
    (parameters: fetchInstagramParameters) => {
      purgeInstagramCache();

      // Used for the filtering of the results
      setSearchParameters(parameters);

      fetchInstagramPosts(parameters)
        .then(() => {
          setShowFilters(true);
          setError(undefined);
          onSearchResult(parameters, instagramProfiles, !!parameters.isExternal, thirdPartyProfiles);
        })
        .catch(error => {
          const apolloErrorType = getApolloErrorMessage(error);
          const remappedError = remapApolloErrors(error as ApolloError);

          setApolloErrorKey(apolloErrorType);
          setError(remappedError);

          // we have cases where the cache will presist or re-emerge so we always want it
          // empty if there is an error
          purgeInstagramCache();
        });
    },
    [
      fetchInstagramPosts,
      purgeInstagramCache,
      setError,
      setSearchParameters,
      setShowFilters,
      onSearchResult,
      instagramProfiles,
      thirdPartyProfiles
    ]
  );

  const getThirdPartyProfiles = useCallback(
    (value: string) => {
      const profileWithGraphApi = instagramProfiles.find(
        p => p.connected && p.apiType === InstagramApiType.FACEBOOK_GRAPH
      );

      // If we don't have a valid search profile, return
      if (!profileWithGraphApi) {
        showToast({
          severity: 'error',
          summary: 'No Active Profile',
          detail:
            'You currently do not have an active profile to search against. Please make sure your Instgram Profiles are re-connected'
        });

        return;
      }

      fetchThirdPartyProfiles(String(profileWithGraphApi?.id), value).catch(error => {
        setError(remapApolloErrors(error as ApolloError));
      });
    },
    [fetchThirdPartyProfiles, instagramProfiles, setError]
  );

  const submitFormHandler = useCallback(
    (search: IGSearchTypesEnum | InstagramSearchType, value: string) => {
      const socialProfileId = value;
      const externalId = String(instagramProfiles.find(p => p.id === value)?.username);

      setError(undefined);
      clearFilterBarForm();

      const baseParameters = {
        socialProfileId,
        externalId,
        sortType: formInstance.getValues('sortType') ?? InstagramSearchSortType.LATEST,
        term: formInstance.getValues('hashtags') ?? ''
      };

      switch (search) {
        case InstagramSearchType.PROFILE:
          getInstagramPosts({
            ...baseParameters,
            searchType: InstagramSearchType.PROFILE
          });
          break;
        case IGSearchTypesEnum.BUSINESS_PROFILE_POSTS:
          getInstagramPosts({
            ...baseParameters,
            socialProfileId: instagramProfiles.filter(p => p.apiType === InstagramApiType.FACEBOOK_GRAPH)[0].id,
            externalId: value,
            searchType: InstagramSearchType.PROFILE,
            isExternal: true
          });
          break;
        case InstagramSearchType.MENTIONS:
          getInstagramPosts({
            ...baseParameters,
            searchType: InstagramSearchType.MENTIONS
          });
          break;
        case IGSearchTypesEnum.BUSINESS_PROFILES:
          if (value) {
            getThirdPartyProfiles(value);
          }
          break;
        default:
          // Don't show filters if we fall through
          setShowFilters(true);
          break;
      }
    },
    [
      clearFilterBarForm,
      formInstance,
      getInstagramPosts,
      getThirdPartyProfiles,
      instagramProfiles,
      setError,
      setShowFilters
    ]
  );

  const filterSearchHandler = useCallback(
    (resetFilterBar?: boolean) => {
      if (resetFilterBar) {
        clearFilterBarForm();
      }

      if (searchParameters) {
        purgeInstagramCache();
        setError(undefined);

        setSearchStep(SearchStepEnum.TWO);

        const searchType =
          !formInstance.getValues('type') || formInstance.getValues('type') === IGSearchTypesEnum.BUSINESS_PROFILES
            ? InstagramSearchType.PROFILE
            : formInstance.getValues('type');

        const parameters: fetchInstagramParameters = {
          ...searchParameters,
          searchType,
          sortType: formInstance.getValues('sortType') ?? InstagramSearchSortType.LATEST,
          term: formInstance.getValues('term') ?? ''
        };

        getInstagramPosts(parameters);
      }
    },
    [
      searchParameters,
      clearFilterBarForm,
      purgeInstagramCache,
      setError,
      setSearchStep,
      formInstance,
      getInstagramPosts
    ]
  );

  const getInstagramInputArgs = useCallback((): ImportSourceArgsGroupInput => {
    const { type, sortType, term } = formInstance.getValues();

    const searchType = type === IGSearchTypesEnum.BUSINESS_PROFILES ? InstagramSearchType.PROFILE : type;
    const socialProfileId = searchParameters?.socialProfileId ?? instagramProfiles[0].id;

    const instagramProfile = instagramProfiles.find(p => p.id === socialProfileId);
    const externalProfiles = getAllApolloCacheEntitiesOfType(cache, GraphQLTypes.ThirdPartyProfile);

    const thirdPartyProfileDisplayName =
      type === IGSearchTypesEnum.BUSINESS_PROFILES
        ? externalProfiles.find(p => p.username === searchParameters?.externalId)?.displayName
        : instagramProfile?.displayName;

    const externalId =
      searchParameters?.externalId ?? instagramProfiles.find(p => p.id === socialProfileId)?.instagramId;

    const requiredArgs = {
      socialProfileId,
      externalId,
      searchType,
      sortType,
      thirdPartyProfileDisplayName,
      term
    };

    const args = {
      ...requiredArgs
    };

    return { instagram: args };
  }, [cache, formInstance, instagramProfiles, searchParameters]);

  useEffect(() => {
    setShowFilters(false);
    setError(undefined);
    purgeInstagramCache();
    purgeThirdPartyProfiles();
    clearInstagramForm(typeValue);
    setSearchParameters(undefined);
    setSelectedAutoCompleteValue(undefined);
  }, [
    clearInstagramForm,
    purgeInstagramCache,
    purgeThirdPartyProfiles,
    setError,
    setSearchParameters,
    setShowFilters,
    typeValue
  ]);

  const isOwnProfile = useCallback(
    (externalId: string) => {
      return !!instagramProfiles.find(p => p.username === externalId);
    },
    [instagramProfiles]
  );

  const resetCallBackHandler = useCallback(() => {
    clearInstagramForm('type');
    setSelectedPosts([]);
    purgeInstagramCache();
    setSelectedAutoCompleteValue(undefined);
    setSearchStep(SearchStepEnum.ONE);
    setHasSearched(false);
    setShowFilters(false);
  }, [clearInstagramForm, purgeInstagramCache, setHasSearched, setSearchStep, setSelectedPosts, setShowFilters]);

  // Prefill with SavedSource
  useEffect(() => {
    if (
      !modalType &&
      selectedMenuItem &&
      selectedMenuItem.__typename === 'ImportSource' &&
      selectedMenuItem.args.__typename === 'ImportSourceArgsInstagram'
    ) {
      purgeInstagramCache();

      const { args } = selectedMenuItem;
      setError(undefined);

      const ownProfile = isOwnProfile(String(args.externalId));

      const type =
        args.instagramSearchType === InstagramSearchType.PROFILE && ownProfile
          ? args.instagramSearchType
          : args.instagramSearchType === InstagramSearchType.PROFILE && !ownProfile
          ? IGSearchTypesEnum.BUSINESS_PROFILES
          : args.instagramSearchType;

      const parameters: fetchInstagramParameters = {
        socialProfileId: String(args.socialProfileId),
        externalId: String(args.externalId),
        searchType: args.instagramSearchType,
        sortType: args.instagramSortType,
        term: args.term ?? ''
      };

      formInstance.reset({
        ...parameters,
        platform: SocialOAuthProvider.INSTAGRAM,
        type
      });

      fetchInstagramPosts(parameters)
        .then(() => {
          setShowFilters(true);
          onSearchResult(parameters, instagramProfiles, false);
          setSelectedMenuItem(undefined);
        })
        .catch(error => {
          setError(remapApolloErrors(error as ApolloError));
        })
        .finally(() => {
          setSearchParameters(parameters);

          formInstance.setValue(InstagramSearchType.MENTIONS, String(args.socialProfileId));
          formInstance.setValue(InstagramSearchType.PROFILE, String(args.socialProfileId));
          formInstance.setValue(IGSearchTypesEnum.BUSINESS_PROFILES, String(args.externalId));

          formInstance.setValue('sortType', args.instagramSortType);
          formInstance.setValue('term', String(args.term));

          setSelectedAutoCompleteValue(String(args.externalId));
        });
    }
  }, [
    fetchInstagramPosts,
    formInstance,
    isOwnProfile,
    modalType,
    purgeInstagramCache,
    selectedMenuItem,
    setError,
    setSearchParameters,
    setSearchStep,
    setSelectedMenuItem,
    setShowFilters,
    onSearchResult,
    instagramProfiles
  ]);

  // Reset the form if we change the type of search
  useEffect(() => {
    clearInstagramForm('');
    setSearchStep(SearchStepEnum.ONE);
  }, [clearInstagramForm, setSearchStep, typeValue]);

  // Make sure form is cleared when unmounted
  useDidMount(() => {
    setError(undefined);
    setHasSearched(false);
    setSourceType(PostSourceType.InstagramSocialProfile);

    return () => {
      setSourceType(undefined);
      clearInstagramForm('');
      setSelectedPosts([]);
      purgeInstagramCache();
    };
  });

  const viewProps: ContentFilterInstagramViewProps = {
    ...props,
    submitFormHandler,
    selectedAutoCompleteValue,
    setSelectedAutoCompleteValue,
    filterSearchHandler,
    hasPosts: posts.length > 0,
    showSaveSourceModal,
    setShowSaveSourceModal,
    getInstagramInputArgs,
    instagramProfiles,
    error,
    apolloErrorKey,
    resetCallBackHandler
  };

  return <ContentFilterInstagramView {...viewProps} />;
};
